import React, { useState } from 'react';
import '../index.css';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'What is GamersUnion?',
          answer: 'A place where gamers can discover indie games, indie developers can show off their work, and everyone can participate in giveaways and earn rewards for being active. I built it to help solve the problem of indie games getting buried and gamers struggling to find good titles.'
        },
        {
          question: 'Is it free?',
          answer: 'Yes, everything is free. You can make an account, browse games, enter giveaways, earn points, write reviews, and use the community without paying anything. I keep it running through optional donations and partnerships that make sense.'
        },
        {
          question: 'How do I create an account?',
          answer: 'Click "Register" at the top, pick a username, enter your email, and create a password. You\'ll get a verification email, click the link, and you\'re set. Then you can start exploring games, entering giveaways, and earning points.'
        }
      ]
    },
    {
      category: 'Points & Rewards',
      questions: [
        {
          question: 'How does the points system work?',
          answer: 'You earn points by being active. Write reviews, claim daily rewards, engage with community posts, comment, maintain your streak, all of that gets you points. The system rewards real engagement, not spam. Quality matters more than quantity.'
        },
        {
          question: 'What can I do with points?',
          answer: 'Redeem them in the shop for game keys (Steam, Epic, GOG), profile stuff, premium features, custom badges, and other perks. I update the shop regularly based on what people ask for. Your points never expire, so save up or spend them whenever.'
        },
        {
          question: 'Do points expire?',
          answer: 'Nope, never. Accumulate them over time and use them when you want. I might adjust balances if I catch someone cheating or abusing the system, but otherwise your points are yours.'
        }
      ]
    },
    {
      category: 'Giveaways',
      questions: [
        {
          question: 'How do giveaways work?',
          answer: 'Anyone can host giveaways: developers, creators, community members. To enter, browse the giveaways, click one you like, and hit "Enter." Most cost a few points to enter (keeps out bots and spam). When it ends, winners are picked randomly and notified. Usually you have 48-72 hours to claim your prize before I draw a new winner.'
        },
        {
          question: 'Can I host a giveaway?',
          answer: 'Absolutely. Any registered user can. Go to the Giveaways page, click "Host a Giveaway," fill in the details (prize, entry cost, duration, number of winners), and post it. Great way for developers to promote their game or for anyone to give back to the community.'
        },
        {
          question: 'How are winners picked?',
          answer: 'Completely random. When the giveaway ends, the system uses a secure random generator to pick winners from all valid entries. Winners get emailed and notified on the site. The host delivers the prize. Fair and simple.'
        }
      ]
    },
    {
      category: 'Indie Games',
      questions: [
        {
          question: 'How do I submit my game?',
          answer: 'Create an account, select "Indie Developer" as your type, then go to Indie Games and click "Submit Your Game." Fill in the details: title, description, genre, screenshots, trailer, store links. I review submissions to make sure they fit (usually takes 24-48 hours), then your game goes live.'
        },
        {
          question: 'Does it cost anything to showcase my game?',
          answer: 'No, it\'s free. I don\'t charge submission fees or take revenue cuts. I built this to help indie developers get noticed, not to make money off you. Just engage with the community and you\'re good.'
        },
        {
          question: 'How do I get more visibility?',
          answer: 'Make your listing good: quality screenshots, engaging description, relevant tags. Host giveaways to get attention. Respond to reviews and engage with the community. Share updates through community cards. Get more reviews (games with more reviews show up more). Use hashtags so people can find you. Pretty straightforward.'
        }
      ]
    },
    {
      category: 'Reviews & Ratings',
      questions: [
        {
          question: 'How do I write a review?',
          answer: 'Find a game you\'ve played, click on it, scroll down, and hit "Write a Review." Say what you think, helpful reviews get you points. Be honest. Other people use these to decide if they want to try the game.'
        },
        {
          question: 'Can developers respond to reviews?',
          answer: 'Yes. Developers can reply to reviews on their games. It\'s a good way to show you care about feedback, thank people, or clarify something. Helps build trust with players.'
        },
        {
          question: 'What if I see a fake or spam review?',
          answer: 'Report it. There\'s a report button on every review. I check reports and remove anything fake, spam, or abusive. I want reviews to be genuinely helpful, not just noise.'
        }
      ]
    },
    {
      category: 'Community',
      questions: [
        {
          question: 'What are Community Cards?',
          answer: 'Like posts or updates from members. Share game recommendations, ask questions, show off your setup, whatever. It\'s how people communicate and help each other out here. You can like, comment, and engage with cards to earn points.'
        },
        {
          question: 'How do I report bad behavior?',
          answer: 'Every post, comment, and review has a report button. Click it, tell me what\'s wrong, and I\'ll look into it. Zero tolerance for harassment, spam, or scams. I try to keep this place welcoming.'
        },
        {
          question: 'Can I message other users?',
          answer: 'Yes, there\'s a messaging system. You can DM other members, but respect people\'s boundaries. Spam or harassment in DMs gets you banned just as fast as public stuff.'
        }
      ]
    },
    {
      category: 'Account & Settings',
      questions: [
        {
          question: 'How do I change my account type?',
          answer: 'Go to Settings, find Account Type, and request a change (like switching to Indie Developer). Some changes need approval to prevent abuse, but I usually process them within a day or two.'
        },
        {
          question: 'Can I delete my account?',
          answer: 'Yes. Go to Settings and look for the account deletion option. This is permanent, your data, points, and content get removed. Think about it before you do it.'
        },
        {
          question: 'How do I reset my password?',
          answer: 'Click "Forgot Password" on the login page. Enter your email, and I\'ll send you a reset link. Click it, make a new password, and you\'re back in.'
        }
      ]
    }
  ];

  let questionIndex = 0;

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '50px',
        animation: 'fadeInUp 0.6s ease-out'
      }}>
        <h1 style={{
          color: 'var(--text-accent)',
          fontSize: '42px',
          fontWeight: '800',
          marginBottom: '16px',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          FAQ
        </h1>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '18px',
          lineHeight: '1.6'
        }}>
          Got questions? Here are answers to the most common ones
        </p>
      </div>

      <div style={{ animation: 'fadeInUp 0.8s ease-out' }}>
        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} style={{ marginBottom: '40px' }}>
            <h2 style={{
              color: 'var(--text-accent)',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '2px solid var(--primary)'
            }}>
              {category.category}
            </h2>

            {category.questions.map((faq) => {
              const currentIndex = questionIndex++;
              const isOpen = openIndex === currentIndex;

              return (
                <div 
                  key={currentIndex} 
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '12px',
                    border: `1px solid ${isOpen ? 'var(--primary)' : 'var(--border)'}`,
                    marginBottom: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <button
                    onClick={() => toggleFAQ(currentIndex)}
                    style={{
                      width: '100%',
                      padding: '20px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      color: 'var(--text)',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  >
                    <span>{faq.question}</span>
                    <span style={{
                      fontSize: '20px',
                      color: 'var(--primary)',
                      transition: 'transform 0.3s ease',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{
                      padding: '0 20px 20px 20px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.7',
                      fontSize: '15px',
                      animation: 'fadeIn 0.3s ease-out'
                    }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{
        marginTop: '60px',
        padding: '30px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '16px',
        border: '1px solid var(--border)',
        textAlign: 'center'
      }}>
        <h3 style={{ color: 'var(--text-accent)', fontSize: '22px', marginBottom: '12px' }}>
          Still have questions?
        </h3>
        <p style={{ color: 'var(--text)', marginBottom: '20px' }}>
          Can't find what you're looking for? Reach out to me directly.
        </p>
        <a href="/community" style={{
          display: 'inline-block',
          padding: '12px 30px',
          backgroundColor: 'var(--primary)',
          color: 'white',
          borderRadius: '8px',
          textDecoration: 'none',
          fontWeight: '600'
        }}>
          Ask the Community
        </a>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FAQ;
